/**
 * File: article-redirects.ts
 *
 * The addresses the articles had before they moved to Skapedia (2026-09-29),
 * and where each one is now. Skapedia names its files differently, so every
 * article's address changed with the move: `/articles/2026-09-28` became
 * `/articles/2026-09-28-00`. The old addresses are in search results, in links
 * people shared, and in links inside the articles themselves.
 *
 * It is a closed list: an article published after the move only ever had its
 * Skapedia address. The pairs were matched by article title on 2026-09-29.
 *
 * Imported in TWO places, so it imports nothing:
 *   - at RUNTIME by articles.ts: the article view sends a visitor who opens an
 *     old address to the new one;
 *   - at BUILD time by build/seo-plugins.ts: each old address gets a static
 *     page that points search engines and link previews at the new one.
 *
 * A value is the article's slug on this site, or a full `https://` address
 * when the article now lives on another site. A target that Skapedia does not
 * list (not published, or dated after today) is not redirected to: the old
 * address then answers "Article not found" like any unknown one.
 */

export const ARTICLE_REDIRECTS: Record<string, string> = {
    "2026-08-12": "2026-08-12-00", // Skapi 2.0 is out as a release candidate
    "2026-08-16": "2026-08-16-00", // How Skapi saves you huge on AI tokens
    "2026-09-07-01": "2026-09-04-00", // Skapi 2.0 is out
    "2026-09-07-02": "2026-09-06-00", // How Skapi Encrypts Your Users' Data
    "2026-09-07-03": "2026-09-06-01", // Bandwidth is on us, and Standard just got roomier
    "2026-09-07-05": "2026-09-07-00", // Bonjour, Paris
    "2026-09-16": "2026-09-16-00", // New plan limits, and what happens past them
    "2026-09-28": "2026-09-28-00", // Olá, São Paulo
    "2026-09-28-02": "2026-09-29-00", // The real price of a backend in São Paulo
    "2026-09-28-03": "2026-10-12-00", // Newsletters on Skapi
    "2026-09-28-04": "2026-10-05-00", // Cut the token bill of vibe coding
    "2026-09-29": "2026-09-28-01", // Meet the new Skapi dashboard
    "2026-09-29-02": "2026-09-18-00", // 2.0 was not the finish line
};

/** True for a target on another site (a full address), false for a slug on this one. */
export function isOtherSite(target: string): boolean {
    return /^https:\/\//.test(target);
}

/**
 * Where the old address `slug` leads, or `null` when it never was one. Own
 * keys only: "constructor" and the like are not addresses.
 */
export function redirectOf(slug: string): string | null {
    return Object.prototype.hasOwnProperty.call(ARTICLE_REDIRECTS, slug) ? ARTICLE_REDIRECTS[slug] : null;
}
